import { useState, useId, useMemo } from "react";
import { PageNav } from "@/components/ui/page-nav";
import { SortableHeader } from "@/components/ui/sortable-header";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ExpandedState,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  CheckIcon,
  ChevronDownIcon,
  CornerDownRightIcon,
  MoreVerticalIcon,
  PlusIcon,
  Trash2Icon,
  XIcon,
  CopyPlusIcon,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CopyButton } from "@/components/ui/copy-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Creator } from "@/types/types";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import { useAnalyticsStore } from "@/stores/creators/analyticsStore";
import { useCreatorsStore } from "@/stores";
import { formatCompact } from "@/lib/format";

interface VideoLinkData {
  link_id: string;
  link: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
}

interface GroupedVideoAnalytics {
  campaign_creator_id: string;
  username: string;
  avatar: string;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  engagementRate: number | null;
  links: VideoLinkData[];
  hasData: boolean;
}

function groupByCreator(creators: Creator[]): GroupedVideoAnalytics[] {
  const result: GroupedVideoAnalytics[] = [];

  for (const creator of creators) {
    // Include all creators, even those without uploaded_links
    if (!creator.uploaded_links?.length) {
      result.push({
        campaign_creator_id: creator._id,
        username: creator.username,
        avatar: creator.avatar,
        views: null,
        likes: null,
        comments: null,
        shares: null,
        engagementRate: null,
        links: [],
        hasData: false,
      });
      continue;
    }

    let totalViews = 0;
    let totalLikes = 0;
    let totalComments = 0;
    let totalShares = 0;

    for (const link of creator.uploaded_links) {
      totalViews += link.views || 0;
      totalLikes += link.likes || 0;
      totalComments += link.comments || 0;
      totalShares += link.shares || 0;
    }

    const engagementRate =
      totalViews > 0
        ? ((totalLikes + totalComments + totalShares) / totalViews) * 100
        : 0;

    result.push({
      campaign_creator_id: creator._id,
      username: creator.username,
      avatar: creator.avatar,
      views: totalViews,
      likes: totalLikes,
      comments: totalComments,
      shares: totalShares,
      engagementRate,
      links: creator.uploaded_links,
      hasData: true,
    });
  }

  return result;
}

interface VideoAnalyticsTableProps {
  creators: Creator[];
  /** Read-only mode for public share pages: hides add/delete link affordances. */
  readOnly?: boolean;
}

function calculateEngagement(link: VideoLinkData): number {
  if (!link.views) return 0;
  return ((link.likes + link.comments + link.shares) / link.views) * 100;
}

const columns: ColumnDef<GroupedVideoAnalytics>[] = [
  {
    id: "expand",
    header: "",
    cell: ({ row }) => {
      const hasMultipleLinks = row.original.links.length > 1;
      return hasMultipleLinks ? (
        <ChevronDownIcon
          className={`size-4 transition-transform opacity-60 ${row.getIsExpanded() ? "rotate-180" : ""
            }`}
        />
      ) : null;
    },
    size: 40,
    minSize: 40,
    maxSize: 40,
  },
  {
    accessorKey: "username",
    header: "Creator",
    cell: ({ row }) => {
      const { avatar, username, links, hasData, campaign_creator_id } = row.original;
      return (
        <div className="flex items-center gap-2 min-w-32">
          <Avatar className="size-9">
            <AvatarImage src={avatar} alt={username} />
            <AvatarFallback className="text-xs">
              {username?.charAt(0)?.toUpperCase() || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start justify-center">
            <span className="font-medium">@{username}</span>
            {hasData && links.length > 0 ? (
              <div className="flex items-center text-muted-foreground">
                <CornerDownRightIcon className="size-3.5 inline-block mr-1" />
                <span className="text-xs text-muted-foreground leading-tight">
                  {links.length} video{links.length > 1 ? "s" : ""}
                </span>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground leading-tight">
                No videos uploaded
              </span>
            )}
          </div>
          <span className="ml-auto" onClick={(e) => e.stopPropagation()}>
            <CopyButton
              buttonText=""
              tooltipText="Copy Campaign Creator ID"
              textToCopy={campaign_creator_id}
              variant="ghost"
              tooltipDelay={500}
              className="size-8 hover:bg-muted-foreground/10 text-muted-foreground opacity-0 group-hover/row:opacity-100"
            />
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "views",
    header: ({ column }) => <SortableHeader column={column}>Views</SortableHeader>,
    cell: ({ row }) => {
      const value = row.getValue("views") as number | null;
      return (
        <span className="font-mono text-muted-foreground">
          {value !== null ? formatCompact(value) : "--"}
        </span>
      );
    },
  },
  {
    accessorKey: "likes",
    header: ({ column }) => <SortableHeader column={column}>Likes</SortableHeader>,
    cell: ({ row }) => {
      const value = row.getValue("likes") as number | null;
      return (
        <span className="font-mono text-muted-foreground">
          {value !== null ? formatCompact(value) : "--"}
        </span>
      );
    },
  },
  {
    accessorKey: "comments",
    header: ({ column }) => <SortableHeader column={column}>Comments</SortableHeader>,
    cell: ({ row }) => {
      const value = row.getValue("comments") as number | null;
      return (
        <span className="font-mono text-muted-foreground">
          {value !== null ? formatCompact(value) : "--"}
        </span>
      );
    },
  },
  {
    accessorKey: "shares",
    header: ({ column }) => <SortableHeader column={column}>Shares</SortableHeader>,
    cell: ({ row }) => {
      const value = row.getValue("shares") as number | null;
      return (
        <span className="font-mono text-muted-foreground">
          {value !== null ? formatCompact(value) : "--"}
        </span>
      );
    },
  },
  {
    accessorKey: "engagementRate",
    header: ({ column }) => <SortableHeader column={column}>Eng. Rate</SortableHeader>,
    cell: ({ row }) => {
      const value = row.getValue("engagementRate") as number | null;
      return (
        <span className="font-mono text-muted-foreground">
          {value !== null ? `${value.toFixed(1)}%` : "--"}
        </span>
      );
    },
  },
];


export default function VideoAnalyticsTable({ creators, readOnly = false }: VideoAnalyticsTableProps) {

  const id = useId();
  const { getToken } = useAuth();
  const { createUploadedLink, deleteUploadedLink } = useAnalyticsStore();
  const { setCreators } = useCreatorsStore();

  const [sorting, setSorting] = useState<SortingState>([
    { id: "views", desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [addingLinkFor, setAddingLinkFor] = useState<string | null>(null);
  const [newLinkValue, setNewLinkValue] = useState("");

  const groupedData = useMemo(() => groupByCreator(creators), [creators]);

  const table = useReactTable({
    data: groupedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      pagination,
      expanded,
    },
  });

  async function handleCreateUploadedLink() {
    try {
      const url = new URL(newLinkValue);
      const hostname = url.hostname.toLowerCase();
      if (
        !(
          hostname.includes("instagram.com") ||
          hostname.includes("tiktok.com")
        )
      ) {
        toast.error("Please enter a valid Instagram or TikTok URL.");
        return;
      }
    } catch (_) {
      toast.error("Please enter a valid URL.");
      return;
    }

    //check if link already exists for this creator
    const creator = creators.find((c) => c._id === addingLinkFor);
    if (creator?.uploaded_links?.some((link) => link.link === newLinkValue)) {
      toast.error("This link already exists for this creator.");
      return;
    }

    const token = await getToken();
    if (addingLinkFor && newLinkValue.trim() !== "" && token) {
      const new_link = await createUploadedLink(addingLinkFor, newLinkValue, token);
      if (new_link) {
        // Update creators state with the new link
        const updatedCreators = creators.map((creator) =>
          creator._id === addingLinkFor
            ? {
              ...creator,
              uploaded_links: [...(creator.uploaded_links || []), new_link]
            }
            : creator
        );
        setCreators(updatedCreators);
      }
    }
    toast.success("Link added successfully.", {
      description: "We track video analytics every 24 hours.",
    })
    setAddingLinkFor(null);
    setNewLinkValue("");
  }

  async function handleDeleteUploadedLink(campaign_creator_id: string, link_id: string) {
    const token = await getToken();
    if (token) {
      const deleted = await deleteUploadedLink(campaign_creator_id, link_id, token);
      if (deleted) {
        // Update creators state to remove the deleted link
        const updatedCreators = creators.map((creator) => {
          if (creator._id === campaign_creator_id) {
            return {
              ...creator,
              uploaded_links: creator.uploaded_links?.filter((link) => link.link_id !== link_id) || []
            };
          }
          return creator;
        });
        setCreators(updatedCreators);
        toast.success("Video link deleted successfully.");
      }
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 border rounded-lg overflow-hidden shadow-xs">
        <Table className="table-fixed">
          <TableHeader className="sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-background">
                <TableHead className="w-10" />
                {headerGroup.headers.slice(1).map((header) => (
                  <TableHead
                    key={header.id}
                    className={header.id === "username" ? "w-96" : ""}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const isExpanded = row.getIsExpanded();
                const campaign_creator_id = row.original.campaign_creator_id;
                const isAddingLink = addingLinkFor === campaign_creator_id;
                return (
                  <>
                    <TableRow
                      key={row.id}
                      className={`group/row cursor-pointer ${isExpanded ? "border-b-0" : ""}`}
                      onClick={() => row.toggleExpanded()}
                    >
                      <TableCell className="w-10 px-3">
                        <ChevronDownIcon
                          className={`size-4 transition-transform ${isExpanded ? "rotate-180" : ""
                            }`}
                        />
                      </TableCell>
                      {row
                        .getVisibleCells()
                        .slice(1)
                        .map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                    </TableRow>
                    {isExpanded && (
                      <>
                        {row.original.links.map((link) => (
                          <TableRow
                            key={link.link_id}
                            data-context-menu
                            className="hover group/row border-b-0"
                          >
                            <TableCell className="w-10 px-3"></TableCell>
                            <TableCell className="h-full py-0">
                              <div className="flex items-center gap-1 pl-4 h-11">
                                <a
                                  href={link.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground h-5 flex items-center justify-center hover:text-foreground hover:underline transition-colors truncate text-sm border-l border-muted-foreground/20 border-dashed ps-2 pl-3"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <span className="truncate">
                                    {link.link
                                      .replace("https://", "")
                                      .replace("www.", "")}
                                  </span>
                                </a>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-muted-foreground text-sm">
                                {formatCompact(link.views)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-muted-foreground text-sm">
                                {formatCompact(link.likes)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-muted-foreground text-sm">
                                {formatCompact(link.comments)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-muted-foreground text-sm">
                                {formatCompact(link.shares)}
                              </span>
                            </TableCell>
                            <TableCell className="flex flex-row justify-baseline items-center overflow-hidden">
                              <span className="font-mono text-muted-foreground text-sm">
                                {calculateEngagement(link).toFixed(1)}%
                              </span>
                              {!readOnly && (
                              <div className="flex items-center justify-center ml-auto h-5">
                                <DropdownMenu>
                                  <DropdownMenuTrigger
                                    className="size-7.5 shrink-0 text-muted-foreground hover:text-foreground data-[state=open]:opacity-100 group-hover/row:opacity-100 opacity-0"
                                    asChild
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Button
                                      variant="muted-ghost"
                                      size="icon"
                                     
                                    >
                                      <MoreVerticalIcon className="size-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="text-xs"
                                  >
                                    <DropdownMenuItem
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          link.link
                                        );
                                        toast.success(
                                          "Link copied to clipboard!"
                                        );
                                      }}
                                    >
                                      <CopyPlusIcon />
                                      Copy Link
                                    </DropdownMenuItem>
                                    <DropdownMenuItem variant="destructive" onClick={
                                      () => handleDeleteUploadedLink(campaign_creator_id, link.link_id)
                                    }>
                                      <Trash2Icon />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Add video link row */}
                        {!readOnly && (
                        <TableRow className="hover:bg-background">
                          <TableCell className="w-10 px-3"></TableCell>
                          <TableCell colSpan={6} className="py-2">
                            <div className="flex items-center pl-4">
                              {isAddingLink ? (
                                <div className="flex items-center gap-2 flex-1 border-l border-muted-foreground/20 border-dashed pl-3 animate-in fade-in-0 slide-in-from-right-2 duration-200">
                                  <Input
                                    placeholder="Paste video URL..."
                                    value={newLinkValue}
                                    onChange={(e) =>
                                      setNewLinkValue(e.target.value)
                                    }
                                    className="h-8 text-sm flex-1 max-w-xs"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleCreateUploadedLink();
                                      } else if (e.key === "Escape") {
                                        setAddingLinkFor(null);
                                        setNewLinkValue("");
                                      }
                                    }}
                                  />
                                  <Button
                                    size="icon"
                                    variant="success-ghost"
                                    className="size-7.5 ml-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCreateUploadedLink();
                                    }}
                                  >
                                    <CheckIcon className="size-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="destructive-ghost"
                                    className="size-7.5"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddingLinkFor(null);
                                      setNewLinkValue("");
                                    }}
                                  >
                                    <XIcon className="size-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex flex-row items-center h-8">
                                  <div className="border-l border-dashed max-h-5 min-h-5" />
                                  <Button
                                    variant="muted-ghost"
                                    size="sm"
                                    className="ml-1 h-8 hover:text-foreground"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddingLinkFor(campaign_creator_id);
                                    }}
                                  >
                                    <PlusIcon className="size-4" />
                                    Add video link
                                  </Button>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        )}
                      </>
                    )}
                  </>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="w-auto flex items-center justify-between gap-5 mt-2">
        <div className="flex items-center gap-3">
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger id={id} className="w-fit whitespace-nowrap h-7.5">
              <SelectValue placeholder="Select number of results" />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((pageSize) => (
                <SelectItem
                  key={pageSize}
                  value={pageSize.toString()}
                  className="h-7.5"
                >
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label htmlFor={id} className="max-sm:sr-only">
            Rows per page
          </Label>
        </div>

        <PageNav
          className="grow justify-end gap-3"
          page={table.getState().pagination.pageIndex + 1}
          pageCount={table.getPageCount()}
          total={table.getRowCount()}
          pageSize={table.getState().pagination.pageSize}
          onFirst={() => table.firstPage()}
          onPrevious={() => table.previousPage()}
          onNext={() => table.nextPage()}
          onLast={() => table.lastPage()}
        />
      </div>
    </div>
  );
}
